import React, { useEffect } from "react";
import { I18N_NS } from "Config/i18n";
import { useTranslation } from "react-i18next";
import { TableWithRadios, TableWithRadiosColumnProps } from "Components/ScrollableTable/TableWithRadios";
import { dayjs } from "Config/dayjs";
import { Button, Space } from "antd";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { onlineViewerStore } from "Stores/OnlineViewerStore";
import { Loader } from "Components/General/Loader";
import { nanoid } from "nanoid";

export interface OnlineViewerCompareProps extends ModalComponentProps {
  files: API_FileDataEntity[];
  fileToCompare: API_FileDataEntity;
}

export const OnlineViewerCompare = ({ files, hideModal, fileToCompare }: OnlineViewerCompareProps) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [selectedRow, setSelectedRow] = React.useState<API_FileDataEntity>();
  const [data, setData] = React.useState<(API_FileDataEntity & { uid: string })[]>();
  const columns: TableWithRadiosColumnProps<API_FileDataEntity>[] = [
    {
      dataKey: "name",
      label: t("Filename"),
      width: 200,
      flexGrow: 1,
    },
    {
      dataKey: "index",
      label: t("Index"),
      width: 100,
    },
    {
      dataKey: "created",
      label: t("Last change"),
      width: 170,
      cellRenderer: ({ cellData }) => dayjs(+cellData).format("lll"),
    },
  ];

  const handleCompare = async () => {
    try {
      await onlineViewerStore.compareFiles(selectedRow, fileToCompare);
      hideModal();
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    if (fileToCompare.state === "ACTIVE") {
      setData(files.filter((item) => item.state !== "ACTIVE").map((item) => ({ ...item, uid: nanoid() })));
    } else {
      setData(files.filter((item) => item.state === "ACTIVE").map((item) => ({ ...item, uid: nanoid() })));
    }
  }, []);

  return (
    <div style={{ display: "flex", height: "100%", flexDirection: "column" }}>
      <Space className="mb-md">
        {t("Please pick another index of the same file for a comparison with")}
        <span>
          <strong>{fileToCompare.name}</strong>:
        </span>
      </Space>
      <div className="scrollable-table-wrapper">
        {data ? (
          <TableWithRadios<API_FileDataEntity & { uid: string }>
            rowKey={"uid"}
            rowCount={data.length}
            columns={columns}
            getRadioProps={({ viewers = {} }) => ({ disabled: !viewers.onlineViewerCompare })}
            onSelectRow={setSelectedRow}
            defaultSelectedRowKey={data[0]?.uid}
            data={data}
          />
        ) : (
          <Loader />
        )}
        <ButtonsRow justify="space-between">
          <Button onClick={hideModal}>{t("Cancel")}</Button>
          <Button onClick={handleCompare} disabled={!selectedRow} type="primary">
            {t("Select")}
          </Button>
        </ButtonsRow>
      </div>
    </div>
  );
};
